import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Search, 
  ArrowUpDown, 
  RefreshCw, 
  PlusCircle, 
  AlertCircle, 
  X
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserUseCasesList() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [useCases, setUseCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortField, setSortField] = useState<'created_at' | 'number' | 'title' | 'current_step'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Selected Detail Modal
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchUseCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (stageFilter) params.append('stage', stageFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      params.append('sort_by', sortField);
      params.append('sort_order', sortOrder);

      const res = await fetch(`/api/user/use-cases?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error(`Failed to load use cases: ${res.statusText}`);
      }

      const data = await res.json();
      if (data.success) {
        setUseCases(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to fetch use cases');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUseCases();
  }, [session, statusFilter, stageFilter, priorityFilter, sortField, sortOrder]);

  // Load single use case detail
  const openDetail = async (id: string) => {
    try {
      setSelectedCaseId(id);
      setDetailLoading(true);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch(`/api/user/use-cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDetailData(data.data);
      } else {
        alert(data.error || 'Unable to open case');
        setSelectedCaseId(null);
      }
    } catch (e: any) {
      alert(e.message);
      setSelectedCaseId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Client-side search debounce / immediate filtering
  const filteredUseCases = useMemo(() => {
    if (!search) return useCases;
    const q = search.toLowerCase();
    return useCases.filter(uc => 
      uc.title?.toLowerCase().includes(q) ||
      uc.number?.toLowerCase().includes(q) ||
      uc.description?.toLowerCase().includes(q) ||
      uc.mitre_technique?.toLowerCase().includes(q)
    );
  }, [useCases, search]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            <span>My Detection Use Cases</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Directory of SIEM detection rules authored by or assigned to your identity
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchUseCases}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/user/use-cases/create')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-500/20"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            <span>Create Use Case</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* FILTER & SEARCH BAR */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 p-4 rounded-xl border border-border bg-card shadow-xs">
        {/* Search */}
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by title, number, MITRE technique..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Status filter */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="CREATED">CREATED</option>
            <option value="VALIDATED">VALIDATED</option>
            <option value="COORDINATION_PENDING">COORDINATION_PENDING</option>
            <option value="SHIFT_LEAD_ASSIGNMENT">SHIFT_LEAD_ASSIGNMENT</option>
            <option value="IN_PROGRESS">IN_PROGRESS</option>
            <option value="SHIFT_LEAD_VERIFICATION">SHIFT_LEAD_VERIFICATION</option>
            <option value="SHIFT_LEAD_REVIEW">SHIFT_LEAD_REVIEW</option>
            <option value="TECHNICAL_REVIEW">TECHNICAL_REVIEW</option>
            <option value="UAT_APPROVED">UAT_APPROVED</option>
            <option value="UAT_DEPLOYED">UAT_DEPLOYED</option>
            <option value="WEEKLY_REVIEW_COMPLETED">WEEKLY_REVIEW_COMPLETED</option>
            <option value="POST_MEETING_REPORT">POST_MEETING_REPORT</option>
            <option value="FINAL_APPROVED">FINAL_APPROVED</option>
            <option value="PRODUCTION_APPROVED">PRODUCTION_APPROVED</option>
            <option value="PRODUCTION_DEPLOYED">PRODUCTION_DEPLOYED</option>
          </select>
        </div>

        {/* Stage Filter */}
        <div>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All 15 Stages</option>
            {Array.from({ length: 15 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>Step {i + 1}</option>
            ))}
          </select>
        </div>

        {/* Priority filter */}
        <div>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Priorities</option>
            <option value="Urgent">Urgent / Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {/* USE CASES TABLE */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Loading live Use Case records...</span>
          </div>
        ) : filteredUseCases.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground">
            <Shield className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-foreground">No Use Cases match your filters</p>
            <p className="text-xs mt-1">Try resetting the search filters or create a new detection rule.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                <tr>
                  <th 
                    className="py-3 px-4 font-semibold cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('number')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Use Case Number</span>
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th 
                    className="py-3 px-4 font-semibold cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Title</span>
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th 
                    className="py-3 px-4 font-semibold cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('current_step')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Current Stage</span>
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Priority</th>
                  <th 
                    className="py-3 px-4 font-semibold cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Created Date</span>
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-semibold">Updated Date</th>
                  <th className="py-3 px-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUseCases.map((uc) => (
                  <tr 
                    key={uc.id} 
                    className="hover:bg-muted/30 transition-colors cursor-pointer group"
                    onClick={() => openDetail(uc.id)}
                  >
                    <td className="py-3 px-4 font-mono font-bold text-blue-500 group-hover:underline">
                      {uc.number}
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground max-w-xs truncate">
                      {uc.title}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-foreground border border-border">
                        Step {uc.current_step}: {uc.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20">
                        {uc.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold",
                        uc.priority === 'High' ? "bg-amber-500/15 text-amber-500" :
                        uc.priority === 'Urgent' ? "bg-rose-500/15 text-rose-500" : "bg-blue-500/15 text-blue-500"
                      )}>
                        {uc.priority}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                      {new Date(uc.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                      {new Date(uc.updated_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(uc.id);
                        }}
                        className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-muted text-foreground hover:bg-blue-600 hover:text-white transition-colors"
                      >
                        Open Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAIL MODAL DRAWER */}
      {selectedCaseId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-border bg-muted/40">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white font-bold text-xs shadow-md">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-blue-500">{detailData?.number || 'Loading...'}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 font-semibold border border-blue-500/20">
                      Step {detailData?.current_step}: {detailData?.status}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-foreground mt-0.5 line-clamp-1">{detailData?.title}</h3>
                </div>
              </div>

              <button
                onClick={() => setSelectedCaseId(null)}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar text-xs">
              {detailLoading ? (
                <div className="p-12 flex flex-col items-center justify-center text-muted-foreground gap-3">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                  <span>Loading full specifications from Backend API...</span>
                </div>
              ) : detailData ? (
                <>
                  {/* Overview Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-muted/30 border border-border">
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-semibold">Priority</span>
                      <p className="font-bold text-foreground mt-0.5">{detailData.priority}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-semibold">Workspace</span>
                      <p className="font-bold text-foreground mt-0.5">{detailData.workspace || 'Pinochle-IND'}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-semibold">Author</span>
                      <p className="font-bold text-foreground mt-0.5">{detailData.creator}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-semibold">SLA Status</span>
                      <p className="font-bold text-emerald-500 mt-0.5">{detailData.sla_status || 'ON_TRACK'}</p>
                    </div>
                  </div>

                  {/* Description & Objective */}
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">Description</h4>
                      <p className="mt-1 text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {detailData.description || 'No description provided.'}
                      </p>
                    </div>

                    {detailData.detection_objective && (
                      <div>
                        <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">Detection Objective</h4>
                        <p className="mt-1 text-muted-foreground leading-relaxed">
                          {detailData.detection_objective}
                        </p>
                      </div>
                    )}

                    {detailData.business_justification && (
                      <div>
                        <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">Business Justification</h4>
                        <p className="mt-1 text-muted-foreground leading-relaxed">
                          {detailData.business_justification}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* MITRE & Telemetry */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div className="p-3 rounded-lg border border-border bg-card">
                      <span className="text-[10px] font-bold text-blue-500 uppercase">MITRE ATT&CK</span>
                      <p className="font-semibold text-foreground mt-1">{detailData.mitre_tactic || 'Credential Access'}</p>
                      <p className="text-muted-foreground text-[11px] mt-0.5">{detailData.mitre_technique || 'T1558'}</p>
                    </div>

                    <div className="p-3 rounded-lg border border-border bg-card">
                      <span className="text-[10px] font-bold text-purple-500 uppercase">Telemetry / Log Source</span>
                      <p className="font-semibold text-foreground mt-1">{detailData.log_source || detailData.required_logs || 'Windows Security Events'}</p>
                      <p className="text-muted-foreground text-[11px] mt-0.5">Data Source: {detailData.data_source || 'Microsoft Sentinel'}</p>
                    </div>
                  </div>

                  {/* Governance Stakeholders */}
                  <div className="p-4 rounded-xl border border-border bg-card space-y-2">
                    <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">Assigned Stakeholders</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px]">
                      <div>
                        <span className="text-muted-foreground">Assigned Leads:</span>
                        <p className="font-medium text-foreground">{detailData.assigned_leads || 'Logesh & Heman'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Coordinators:</span>
                        <p className="font-medium text-foreground">{detailData.coordinators || 'Praveen & Seshadri'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Active Engineer:</span>
                        <p className="font-medium text-foreground">{detailData.active_engineer_name || 'Not yet assigned'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Workflow History Audit */}
                  {Array.isArray(detailData.history) && detailData.history.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-border">
                      <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">Stage Progression History</h4>
                      <div className="space-y-2">
                        {detailData.history.map((h: any, idx: number) => (
                          <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/20">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 font-bold text-[10px]">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-foreground text-xs">{h.action}</span>
                                <span className="text-[10px] text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                              </div>
                              <p className="text-muted-foreground text-[11px] mt-0.5">
                                Performed by <strong className="text-foreground">{h.performed_by}</strong> ({h.role || 'User'})
                              </p>
                              {h.comments && (
                                <p className="text-foreground text-[11px] mt-1 p-2 rounded bg-background border border-border">
                                  {h.comments}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/20">
              <button
                onClick={() => setSelectedCaseId(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
